(() => {
  if (customElements.get('sac-parts-search')) return;
  const products = new Map();
  const encodeQuery = query => encodeURIComponent(query.replace(/([^\s-])(-+)/g, '$1$2-').replace(/([^\s-]) ([^\s-])/g, '$1-$2'));

  class SacPartsSearch extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.input = this.querySelector('input[type="search"]');
      this.form = this.querySelector('form');
      this.results = this.querySelector('[data-live-results]');
      this.status = this.querySelector('[role="status"]');
      this.viewAll = this.querySelector('[data-view-results]');
      this.dialog = this.closest('dialog');
      this.details = this.closest('details.sac-header__search');
      this.onInput = () => {
        this.onClose();
        if (this.details && !this.details.open) return;
        const query = this.input.value.trim(), vehicle = this.vehicle();
        this.viewAll.href = this.destination(query, vehicle);
        this.viewAll.hidden = query.length < 2;
        if (query.length >= 2) this.timer = setTimeout(() => this.search(query, this.sequence, vehicle), 250);
      };
      this.onSubmit = event => {
        event.preventDefault();
        const query = this.input.value.trim();
        if (query) window.location.assign(this.destination(query, this.vehicle()));
      };
      this.onViewAll = () => { this.viewAll.href = this.destination(this.input.value.trim(), this.vehicle()); };
      this.onKeydown = event => {
        const first = this.results.querySelector('a[href]');
        if (event.key === 'ArrowDown' && first) { event.preventDefault(); first.focus(); }
      };
      this.onClose = () => {
        this.cancel();
        this.status.textContent = '';
        this.results.replaceChildren();
        this.viewAll.hidden = true;
      };
      this.onToggle = () => this.details.open ? this.onInput() : this.onClose();
      this.onEscape = event => {
        if (event.key === 'Escape' && this.details?.open) {
          event.preventDefault();
          this.details.open = false;
          this.onClose();
          this.details.querySelector('summary')?.focus();
        }
      };
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', this.onKeydown);
      this.form.addEventListener('submit', this.onSubmit);
      this.viewAll.addEventListener('click', this.onViewAll);
      this.dialog?.addEventListener('close', this.onClose);
      this.details?.addEventListener('toggle', this.onToggle);
      this.details?.addEventListener('keydown', this.onEscape);
      if (this.input.value.trim()) this.onInput();
    }

    vehicle() {
      try {
        const app = window.Convermax;
        if (app?.isVehicleSelected?.() && app.getVehicleRequestParams && app.getVehicleSearchParams) {
          const params = app.getVehicleRequestParams(), route = app.getVehicleSearchParams();
          if (typeof params === 'string' && typeof route === 'string' && params && route) return { params, route };
        }
      } catch {}
      return { params: '', route: '' };
    }

    destination(query, vehicle) {
      const url = new URL(this.dataset.searchUrl, window.location.origin);
      url.search = `?${vehicle.route ? `${vehicle.route}&` : ''}query=${encodeQuery(query)}`;
      return url.href;
    }

    cancel() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.sequence = (this.sequence || 0) + 1;
      this.results?.removeAttribute('aria-busy');
    }

    current(query, sequence, vehicle) {
      return this.isConnected && sequence === this.sequence && this.input.value.trim() === query && this.vehicle().params === vehicle.params;
    }

    productLink(value) {
      try {
        const url = new URL(value, window.location.origin);
        const match = url.pathname.match(/\/products\/([a-z0-9][a-z0-9-]*)\/?$/i);
        if (url.origin === window.location.origin && !url.username && !url.password && match) return { url, handle: match[1] };
      } catch {}
      return null;
    }

    async product(item, handle, signal) {
      const key = `${this.dataset.rootUrl}:${this.dataset.currency}:${this.dataset.country}:${handle}`;
      let cached = products.get(key);
      if (!cached || Date.now() - cached.time > 60000) {
        const endpoint = new URL(`${(this.dataset.rootUrl || '/').replace(/\/+$/, '')}/products/${handle}.js`, window.location.origin);
        const response = await fetch(endpoint, { signal });
        if (!response.ok) throw new Error('Product unavailable');
        cached = { value: await response.json(), time: Date.now() };
      }
      const product = cached.value, id = String(item.id);
      if (!/^[1-9]\d*$/.test(id) || String(product.id) !== id || !Number.isInteger(product.price_min) || !Number.isInteger(product.price_max) || product.price_min < 0 || product.price_max < product.price_min) return null;
      products.delete(key);
      products.set(key, cached);
      if (products.size > 30) products.delete(products.keys().next().value);
      return product;
    }

    card(item, link, product) {
      const row = document.createElement('li'), anchor = document.createElement('a');
      row.className = 'sac-parts-search__result';
      anchor.className = 'sac-parts-search__product';
      anchor.href = link.url.href;
      try {
        const url = new URL(item.image, window.location.origin);
        if (typeof item.image === 'string' && /^https?:$/.test(url.protocol) && !url.username && !url.password) {
          const image = document.createElement('img');
          image.src = url.href;
          image.alt = '';
          image.width = image.height = 64;
          image.loading = 'lazy';
          anchor.append(image);
        }
      } catch {}
      const copy = document.createElement('span'), title = document.createElement('strong');
      title.textContent = String(item.title || '').replace(/<\/?b>/g, '');
      copy.append(title);
      if (product) {
        const price = document.createElement('span');
        price.className = 'sac-parts-search__price';
        const money = new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(product.price_min / 100);
        price.textContent = product.price_min !== product.price_max ? `${this.dataset.fromPrice || ''} ${money}`.trim() : money;
        copy.append(price);
      }
      anchor.append(copy);
      row.append(anchor);
      const badge = product && this.querySelector('[data-entry-badge-template]')?.content.querySelector('sac-entry-badge')?.cloneNode(true);
      if (badge) {
        Object.assign(badge.dataset, { productId: String(product.id), priceMin: String(product.price_min), priceMax: String(product.price_max) });
        row.append(badge);
      }
      return row;
    }

    async search(query, sequence, vehicle) {
      const controller = this.controller = new AbortController();
      const { signal } = controller, timeout = setTimeout(() => controller.abort(), 10000);
      this.status.textContent = this.dataset.loading;
      this.results.setAttribute('aria-busy', 'true');
      const endpoint = new URL(this.dataset.suggestUrl);
      endpoint.searchParams.set('query', query);
      endpoint.searchParams.set('pagesize', '3');
      endpoint.searchParams.set('page', '0');
      endpoint.searchParams.set('scope', 'Products');
      for (const key of ['country', 'currency', 'locale']) if (this.dataset[key]) endpoint.searchParams.set(`extra.${key}`, this.dataset[key]);
      for (const [key, value] of new URLSearchParams(vehicle.params)) {
        if (/^facet\.\d+\.(field|selection)$/.test(key)) endpoint.searchParams.append(key, value);
      }
      try {
        const response = await fetch(endpoint, { signal, credentials: 'omit' });
        if (!response.ok) throw new Error('Search unavailable');
        const data = await response.json();
        const states = String(data.State).toLowerCase().split(',').map(value => value.trim());
        if (!Array.isArray(data.Items) || !states.every(value => ['normal', 'corrected', 'phonetic', 'partial', 'nothing'].includes(value))) throw new Error('Search response unavailable');
        if (!this.current(query, sequence, vehicle)) return;
        const matches = data.Items.map(item => ({ item, link: this.productLink(item.url) })).filter(({ link }) => link).slice(0, 3);
        const rows = await Promise.all(matches.map(async ({ item, link }) => this.card(item, link, await this.product(item, link.handle, signal).catch(() => null))));
        if (!this.current(query, sequence, vehicle)) return;
        const list = document.createElement('ul');
        list.className = 'sac-parts-search__list';
        list.setAttribute('aria-label', this.dataset.resultsLabel || '');
        list.append(...rows);
        this.results.replaceChildren(list);
        this.status.textContent = rows.length ? this.dataset.count.replace('__COUNT__', String(rows.length)) : this.dataset.empty;
      } catch {
        if (this.current(query, sequence, vehicle)) this.status.textContent = this.dataset.error;
      } finally {
        clearTimeout(timeout);
        if (sequence === this.sequence) this.results.removeAttribute('aria-busy');
      }
    }

    disconnectedCallback() {
      this.cancel();
      this.input?.removeEventListener('input', this.onInput);
      this.input?.removeEventListener('keydown', this.onKeydown);
      this.form?.removeEventListener('submit', this.onSubmit);
      this.viewAll?.removeEventListener('click', this.onViewAll);
      this.dialog?.removeEventListener('close', this.onClose);
      this.details?.removeEventListener('toggle', this.onToggle);
      this.details?.removeEventListener('keydown', this.onEscape);
    }
  }
  customElements.define('sac-parts-search', SacPartsSearch);
})();
